import {
    OrganizationRoomGrant,
    SpaceAgreement,
} from '../../api/organization/organizationApiTypes';

/**
 * The derived rules the Rooms & agreements tab renders.
 *
 * Since #2154 there is **no client-side derivation of "has this agreement
 * ended?"** left here. The predicate lives once, on the server, in
 * `new-backend/admin/src/lib/organizations/agreementLifecycle.ts`, and arrives
 * as `hasEnded` on the DTO. What remains below are compositions of that field
 * with grant data — not second opinions about it.
 *
 * The copy that used to live here was marked interim from the day it was
 * written, for exactly this moment. It is deleted rather than kept as a
 * fallback: two implementations of one business question is how one of them
 * drifts, and this one drifting has a specific, silent failure mode — the date
 * is authoritative and the status is only additional, because **nothing writes
 * `expired`**. A copy that drifted toward `status` alone would become a
 * warning that never fires, which reads as "checked, nothing wrong".
 */

/**
 * Is a room dedicated without an agreement behind it?
 *
 * Allowed on purpose (ADR-0016 Amendment 5 addendum, decision 1): requiring an
 * agreement would require a `commercial_model`, i.e. a price, which an
 * ADR-0012 mode-`external` tenant invoicing rent from their own ERP would have
 * to invent. It is *incomplete*, not invalid — the room is held, but nothing
 * records on what terms.
 *
 * Stays a client rule because it has no server twin: it reads two fields of the
 * grant the page already holds and asserts nothing the API also decides. If the
 * stricter rule is ever adopted ("require an agreement when
 * `invoiceIssuer = kolula`"), it becomes a fourth invariant in the backend's
 * `checkAccessInvariants` and this stays as the display of it.
 */
export const isDedicatedWithoutAgreement = (grant: OrganizationRoomGrant): boolean =>
    grant.accessMode === 'dedicated' && grant.spaceAgreementId === null;

/**
 * Three-valued on purpose: `unknown` is not `running`.
 *
 * `hasEnded` is absent only when the response came from a deployment that
 * predates #2160. Folding that into "running" would put a confident "nothing
 * wrong" on a screen that has not checked — the #1917 / #1060 shape, and the
 * precise thing item 3 of #2154 exists to avoid. So it is carried as its own
 * state and rendered as n/a.
 */
export type AgreementEndedState = 'ended' | 'running' | 'unknown';

export const agreementEndedState = (
    agreement: Pick<SpaceAgreement, 'hasEnded'>,
): AgreementEndedState => {
    if (agreement.hasEnded === undefined) return 'unknown';
    return agreement.hasEnded ? 'ended' : 'running';
};

/**
 * The agreements needing attention: ended, but still holding rooms.
 *
 * An ended agreement with no rooms left is finished business and says nothing.
 * `unknown` never raises the banner — an alarm the screen cannot substantiate
 * is worse than none; the n/a in the row is where that shows.
 *
 * Both halves come from the server now, so the banner cannot disagree with the
 * rows beside it. `heldRoomCount` in particular is NOT interchangeable with
 * "grants pointing at this agreement", which is what this used to count: a
 * `shared` grant may carry an agreement id too, and counting it would claim a
 * room is held when it is merely priced.
 */
export const endedAgreementsStillHoldingRooms = (
    agreements: SpaceAgreement[],
): SpaceAgreement[] => agreements.filter(
    (agreement) => agreement.hasEnded === true && (agreement.heldRoomCount ?? 0) > 0,
);

/**
 * The agreements that BLOCK archiving the organization (#2165 §4).
 *
 * Archive answers *"stop showing me this"*; cancelling an agreement answers
 * *"the relationship ends, and here is when"*. Merging them loses the notice
 * period — a contract cancelled in August effective 31 December is a live,
 * paying customer for four more months, and archiving it on the spot hides the
 * customer whose calls the operator still has to take.
 *
 * So archive survives only for the case with no contract to end: an
 * organization created by mistake, a duplicate, or a prospect that never
 * signed.
 *
 * ⚠️ **This is the display of a rule the API also enforces**, not the rule
 * itself — `archiveBlockingAgreements` in
 * `new-backend/admin/src/lib/organizations/agreementLifecycle.ts` is the
 * authority (ADR-0020: rules are enforced at the API boundary). This copy
 * exists so the operator learns the reason *before* clicking rather than from a
 * 409 afterwards; the two must answer identically, and each names the other.
 * Since #2154 they cannot drift on the *ended* half at all: both read the same
 * derivation, one over the wire.
 *
 * `unknown` BLOCKS. Not knowing whether a contract is running is not permission
 * to archive, and the API refuses it anyway — so the conservative answer costs
 * a false block at worst, while the permissive one hides a paying customer.
 *
 * An agreement already archived does not block — it is withdrawn, and letting a
 * soft-deleted row hold the organization would be an unarchivable dead end.
 * An agreement under notice DOES block: it is running until the end date.
 */
export const agreementsBlockingArchive = (
    agreements: SpaceAgreement[],
): SpaceAgreement[] => agreements.filter((agreement) => isAgreementBackingBenefits(agreement));

/**
 * Was this agreement's ending CONFIRMED by a human? (#2165 §4, PO 2026-08-24.)
 *
 * The client twin of `isConfirmedEnded` in `agreementLifecycle.ts`, and
 * deliberately narrower than `hasEnded`. The gap between them is the rule:
 *
 * A passed `expiryDate` is a **flag, not a conclusion**. Contracts are renewed
 * on paper and entered late, so a lapsed date means "somebody should look" far
 * more often than "this relationship is over". On 1 January an agreement that
 * ran to 31 December reads *Ended* and is flagged — but nothing has happened,
 * and the operator either confirms by cancelling or edits the term because
 * there is a follow-on contract.
 *
 * `unknown` (`hasEnded` absent, pre-#2160 deployment) is NOT confirmed, so it
 * blocks — not knowing is never permission to archive.
 */
const isConfirmedEnded = (agreement: SpaceAgreement): boolean => {
    if (agreement.hasEnded !== true) return false;
    const status = String(agreement.status ?? '').toLowerCase();
    if (status === 'canceled' || status === 'expired') return true;
    if (!agreement.canceledAt) return false;
    return !Number.isNaN(new Date(agreement.canceledAt).getTime());
};

/**
 * Does this agreement still cover the rooms linked to it? The client twin of
 * `isAgreementBackingBenefits` (backend/src/core/utils/agreementLifecycle.ts,
 * mirrored in new-backend/admin), which the server applies when it decides a
 * booking is covered (option C, docs/plans/coverage-per-room-assignment.md).
 *
 * Not archived, and its ending not CONFIRMED. A lapsed date with no notice still
 * covers (#2165) — so the room editor must not hide such a contract, or the one
 * contract that covers a customer's rooms today could not be linked at all.
 * The same predicate is what keeps an organization from being archived.
 */
export const isAgreementBackingBenefits = (agreement: SpaceAgreement): boolean =>
    agreement.archivedAt === null && !isConfirmedEnded(agreement);

/**
 * The three lifecycle states of #2165 §2 — the client twin of
 * `agreementLifecycle` in
 * `new-backend/admin/src/lib/organizations/agreementLifecycle.ts`.
 *
 * ⚠️ **There is deliberately no status enum value for `activeCanceled`.**
 * `canceled` is terminal and the server's `hasEnded` derives from it, so an
 * implementation that wrote `canceled` on notice would report a contract
 * running to 31 December as ended in August. The middle state is `active` plus
 * a future `expiryDate` plus `canceledAt` — three stored facts, one derived
 * label.
 *
 * `ended` wins over `activeCanceled`: once the term is actually over, "notice
 * was given" is history rather than the state to render.
 *
 * The middle state became reachable with the `canceled_at` column (#2154's
 * migration, per `docs/plans/space-agreement-cancellation.md` §3). `null`
 * means no notice was given — which, on an agreement whose date has passed,
 * is what separates a decision from a date nobody renewed.
 */
export const agreementLifecycleOf = (
    agreement: Pick<SpaceAgreement, 'hasEnded' | 'canceledAt'>,
): 'active' | 'activeCanceled' | 'ended' => {
    if (agreement.hasEnded === true) return 'ended';
    if (!agreement.canceledAt) return 'active';
    const canceledAt = new Date(agreement.canceledAt);
    // A malformed value is not a cancellation — it must not promote the row
    // into a state that claims a human decided something.
    return Number.isNaN(canceledAt.getTime()) ? 'active' : 'activeCanceled';
};
